import React from 'react'
import { CancelBtn } from 'components/Button/CancelBtn'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { Animation, Button, ButtonsContainer, Flex, ItemsGrid, TabFooter, ThinSeparator } from 'styling/GlobalStyles'
import { IEmployeesTab } from 'types/users.interface'
import { documentAccessRestrictionsOptions, documentCategoryOptions } from './constants'
import { ISelectValue } from 'constants/globalTypes'
import { BodyText, TitleText } from 'styling/Typography'
import { DropZone } from 'components/DropZone/DropZone'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { SmallTable, SmallThead } from 'components/SmallTable/SmallTable.styled'
import { formatDateForDisplay } from 'utils/helpers'
import { IconContainer } from 'pages/Accomodation/Accommodation.style'
import { FileIcon } from 'assets/icons/FileIcon'
import { OptionsFileIcon } from 'assets/icons/OptionsFileIcon'
import { CloseIcon } from 'assets/icons/CloseIcon'

export const EmployeesNotesTab = ({ state, setState, handleSubmit }: IEmployeesTab) => {

    const addNote = () => {
        setState(prev => ({
            ...prev,
            notes: [
                ...prev.notes,
                { id: new Date().getTime(), content: '', visibilityScope: null, category: null },
            ],
        }))
    }

    const handleNoteTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>, id: number | string) => {
        const { value } = e.target
        setState(prev => ({
            ...prev,
            notes: prev.notes.map(note =>
                (note.id ?? '') === id ? { ...note, content: value } : note,
            ),
        }))
    }

    const handleNoteSelect = (value: ISelectValue, name: string, id: number | string) => {
        setState(prev => ({
            ...prev,
            notes: prev.notes.map(note =>
                (note.id ?? '') === id ? { ...note, [name]: value.value } : note,
            ),
        }))
    }

    const handleDocumentSelect = (value: ISelectValue, name: string, id: number | string) => {
        setState(prev => ({
            ...prev,
            documents: (prev.documents || []).map(doc =>
                (doc.id ?? '') === id ? { ...doc, [name]: value.value } : doc,
            ),
        }))
    }

    const handleDocumentDateChange = (date: Date | null, name: string | undefined, id: number | string) => {
        if (!name) return
        setState(prev => ({
            ...prev,
            documents: (prev.documents || []).map(doc =>
                (doc.id ?? '') === id ? { ...doc, [name]: date } : doc,
            ),
        }))
    }

    const handleDocumentFileChange = (file: File, id: number | string) => {
        setState(prev => ({
            ...prev,
            documents: (prev.documents || []).map(doc =>
                (doc.id ?? '') === id ? { ...doc, documentFile: file } : doc,
            ),
        }))
    }

    const addDocument = () => {
        setState(prev => ({
            ...prev,
            documents: [
                ...(prev.documents || []),
                {
                    id: new Date().getTime(),
                    documentFile: null,
                    category: null,
                    accessRestrictions: null,
                    effectiveDate: null,
                    expiryDate: null,
                } as any,
            ],
        }))
    }

    const removeDocument = (id: number | string) => {
        setState(prev => ({
            ...prev,
            documents: (prev.documents || []).filter(doc => (doc.id ?? '') !== id),
        }))
    }


    const NothingAdded = ({ title }) => {
        return <Flex center justcenter column gap='1rem' >
            <IconContainer>
                <OptionsFileIcon />
            </IconContainer>
            <TitleText>No {title} added</TitleText>
        </Flex>
    }

    return <Animation>
        <Flex gap='1rem' >
            <Flex column width='100%' gap='1rem' >
                <TitleText weight='bold' >Notes</TitleText>
                {state.notes.length ? state.notes.map((note, index) => (
                    <Flex key={note.id ?? index} gap='1rem' column>
                        <Input
                            type='textarea'
                            placeholder='Notes'
                            labelText='Notes'
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                handleNoteTextChange(e, note.id ?? index)
                            }
                            value={note.content}
                            height='100px'
                        />
                        <Flex gap='1rem' >
                            <HonestSelectInput
                                nomarg
                                name='visibilityScope'
                                options={documentCategoryOptions}
                                labelText='Visibility Scope'
                                onChange={(v, n) => handleNoteSelect(v as ISelectValue, n, note.id ?? index)}
                                defaultValue={(note as any)?.visibilityScope}
                            />
                            <HonestSelectInput
                                nomarg
                                name='category'
                                options={documentCategoryOptions}
                                labelText='Category'
                                onChange={(v, n) => handleNoteSelect(v as ISelectValue, n, note.id ?? index)}
                                defaultValue={(note as any)?.category}
                            />
                        </Flex>
                        <ThinSeparator />
                    </Flex>
                )) : <NothingAdded title='Notes' />}
                <Button orange onClick={addNote} >
                    {state.documents.length ? 'Add Another Note' : 'Add Note'}

                </Button>
            </Flex>
            <Flex width='100%' style={{ paddingBottom: '48px' }} >
                <Flex column width='100%' gap='1rem' >
                    <TitleText weight='bold' >Documents</TitleText>

                    {state.documents?.some(d => d.alreadyCreated) && <SmallTable>
                        <SmallThead>
                            <tr>
                                <th>File Name</th>
                                <th>Category</th>
                                <th>Access Restrictions</th>
                                <th>Effective Date</th>
                                <th>Expiration Date</th>
                            </tr>
                        </SmallThead>

                        <tbody>
                            {
                                state?.documents?.filter(d => !d.alreadyCreated).map((d) => {
                                    return (
                                        <tr key={d.id || '/'} >
                                            <td><a href={d.url} target='_blank' rel='noopener noreferrer' > <BodyText style={{ textDecoration: 'underline' }} >{d.fileName || '/'}</BodyText> </a></td>
                                            <td>{documentCategoryOptions.find(o => o.value == d.category)?.label || '/'}</td>
                                            <td>{documentAccessRestrictionsOptions.find(r => r.value == d.accessRestrictions)?.label || '/'}</td>
                                            <td>{formatDateForDisplay(d.effectiveDate) || '/'}</td>
                                            <td>{formatDateForDisplay(d.expiryDate) || '/'}</td>
                                        </tr>
                                    )
                                })}
                        </tbody>
                    </SmallTable>}
                    {(!state.documents?.length || state.documents.every(doc => doc.alreadyCreated)) && (
                        <NothingAdded title='Documents' />
                    )}
                    {(state.documents || []).filter((doc) => !doc.alreadyCreated).map((doc, index) => (
                        <Flex key={doc.id ?? index} column gap='0.75rem'>
                            <Flex width='100%' between >

                                <BodyText weight='bold'>Document {index + 1}</BodyText>
                                <CloseIcon onClick={() => removeDocument(doc.id ?? index)} />
                            </Flex>

                            {doc.url && doc.fileName && (
                                <BodyText>
                                    Current file:{' '}
                                    <a href={doc.url}>
                                        <span style={{ textDecoration: 'underline' }}>{doc.fileName}</span>
                                    </a>
                                </BodyText>
                            )}

                            <BodyText>Add file</BodyText>
                            <DropZone
                                acceptAll
                                file={doc.documentFile}
                                setFile={(file: File) => handleDocumentFileChange(file, doc.id ?? index)}
                            />

                            <ItemsGrid width='100%' columns={2} >
                                <HonestSelectInput
                                    nomarg
                                    name='category'
                                    options={documentCategoryOptions}
                                    labelText='Category'
                                    onChange={(v, n) => handleDocumentSelect(v as ISelectValue, n, doc.id ?? index)}
                                    defaultValue={(doc as any)?.category ?? (doc as any)?.category}
                                />
                                <HonestSelectInput
                                    nomarg
                                    name='accessRestrictions'
                                    options={documentAccessRestrictionsOptions}
                                    labelText='Access Restrictions'
                                    onChange={(v, n) => handleDocumentSelect(v as ISelectValue, n, doc.id ?? index)}
                                    defaultValue={(doc as any)?.accessRestrictions ?? (doc as any)?.accessRestrictions}
                                />
                                <CalendarInput
                                    nomarg
                                    width='100%'
                                    name='effectiveDate'
                                    labelText='Effective Date'
                                    minDate={new Date()}
                                    onChange={(date, name) => handleDocumentDateChange(date, name, doc.id ?? index)}
                                    defaultValue={(doc as any)?.effectiveDate || null}
                                />
                                <CalendarInput
                                    nomarg
                                    width='100%'
                                    name='expiryDate'
                                    minDate={doc.effectiveDate}
                                    labelText='Expiration Date'
                                    onChange={(date, name) => handleDocumentDateChange(date, name, doc.id ?? index)}
                                    defaultValue={(doc as any)?.expiryDate || null}
                                />
                            </ItemsGrid>
                            <ThinSeparator />
                        </Flex>
                    ))}

                    <Button type='button' orange onClick={addDocument}>
                        {state.documents.length ? 'Add Another Document' : 'Add Document'}
                    </Button>
                </Flex>
            </Flex>
        </Flex>
        <TabFooter  >
            <ButtonsContainer>
                <CancelBtn />
                <Button orange onClick={handleSubmit}>
                    Save
                </Button>
            </ButtonsContainer>
        </TabFooter>
    </Animation>
}


